import { Handle, Position } from "@xyflow/react";
import PersonIcon from '@mui/icons-material/Person';
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import ReusableNodes, { NodeContent, NodeError, NodeHandles, NodeHeader, NodeWarning } from "../../../../../components/ReusableNodes";
import { CustomTooltip } from "../../../../../components/TooltipCustomStyle";
import TooltipStatusTitle from "../../../../../components/TooltipStatusTitle";
import ErrorIcon from '@mui/icons-material/Error';
import { getOpenPreviewDRG, getOpenSimulatorDRG } from "../../../../../redux/layoutControlSlice";
import { useSelector } from "react-redux";
import { useCallback } from "react";
import AddIcon from '@mui/icons-material/Add';
import isSelectedAndHighlight from "../../../../../utils/isSelectedAndHighlight";
// import DrgIcon from "../../drgicons/DrgIcon";

const CustomHandle = ({ selected, onAddNode }) => {
    const handleAddNode = (event) => {
        event.stopPropagation(); // ป้องกัน event ไม่ให้ไปถึง onNodeClick ของ React Flow
        onAddNode?.();
    };
    return (
        <Handle
            id="bottom-source"
            type="source"
            position={Position.Bottom}
            style={{
                width: 'auto',
                height: 'auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                paddingTop: '4px'
            }}
        >
            <IconButton
                onClick={handleAddNode}
                sx={{
                    backgroundColor: '#ffffff',
                    border: selected ? '2px solid #4169ff' : '2px solid #9ca3af',
                    width: '0.75rem',
                    height: '0.75rem',
                    padding: '8px',
                    '.group\\/node:hover &': {
                        backgroundColor: '#4169ff',
                        border: '2px solid #ffffff',
                    },
                    '.group\\/node:hover & .MuiSvgIcon-root': {
                        color: '#ffffff',
                    },
                    '&:hover': {
                        backgroundColor: '#4169ff',
                        border: '2px solid #ffffff',
                    },
                    '&:hover .MuiSvgIcon-root': {
                        color: '#ffffff',
                    },
                }}
            >
                <AddIcon
                    sx={{
                        width: 14,
                        height: 14,
                        color: selected ? '#4169ff' : '#6b7280',
                    }}
                />
            </IconButton>
        </Handle>
    );
};

export default function CustomSubjectNode({ id, data, selected, onNodeClick, onNodeDoubleClick, onAddNode }) {

    const theme = useTheme();

    const label = data.general_properties.label;
    const description = data.general_properties.description;

    const openPreviewDRG = useSelector(getOpenPreviewDRG);
    const openSimulatorDRG = useSelector(getOpenSimulatorDRG);
    const warningText = Array.isArray(data?.warning_text) ? data.warning_text : [];
    const errorText = Array.isArray(data?.error_text) ? data.error_text : [];

    const handleNodeClick = (event) => {
        event.stopPropagation(); // ป้องกัน event bubbling
        if (onNodeClick) {
            onNodeClick(event, id);
        }
    };

    const handleDoubleClick = (event) => {
        // สำคัญมาก! ป้องกันไม่ให้ Event ทะลุไปที่ Background ของ React Flow
        // ไม่งั้นมันอาจจะไปทริกเกอร์การ Zoom เมาส์ หรือ Deselect โหนด
        // event.stopPropagation();

        if (onNodeDoubleClick) {
            onNodeDoubleClick(event, id);
        }
    };

    const handleAddNodeFromNode = useCallback(() => {
        if (openSimulatorDRG || openPreviewDRG) {
            return;
        }
        if (onAddNode) {
            onAddNode(id);
        }
    }, [onAddNode, id, openSimulatorDRG, openPreviewDRG]);

    const getOperandType = (operand) => {
        if (Array.isArray(operand)) {
            return 'array';
        }

        if (typeof operand === 'string') {
            return 'string';
        }

        if (typeof operand === 'number') {
            return 'number';
        }

        if (typeof operand === 'boolean') {
            return 'boolean';
        }

        return 'other';
    };

    const formatSingleValue = (value) => {
        if (typeof value === 'string') {
            return `'${value.replace(/'/g, "\\'")}'`;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }

        if (value === null) {
            return 'null';
        }

        if (typeof value === 'undefined') {
            return 'undefined';
        }

        return JSON.stringify(value);
    };

    const formatOperandValue = (operand) => {
        if (Array.isArray(operand)) {
            return `[${operand.map((item) => formatSingleValue(item)).join(',')}]`;
        }

        return formatSingleValue(operand);
    };

    const normalizeDisplayOperand = (operand) => {
        const type = getOperandType(operand);

        const colorByType = {
            string: 'text-rose-600',
            number: 'text-sky-600',
            boolean: 'text-sky-600',
            array: 'text-violet-600',
            other: 'text-slate-600',
        };

        return (
            <span className={`${colorByType[type]} text-sm break-all`}>
                {formatOperandValue(operand)}
            </span>
        );
    };

    const hasConjunction = (value) => value !== null && value !== undefined && value !== '';

    const formatLeftOperand = (operand) => {
        if (typeof operand === 'string' || typeof operand === 'number' || typeof operand === 'boolean') {
            return String(operand);
        }

        if (operand === null) {
            return 'null';
        }

        if (typeof operand === 'undefined') {
            return 'undefined';
        }

        return JSON.stringify(operand);
    };

    const getChildConstraints = (constraints) => {
        if (Array.isArray(constraints?.child)) {
            return constraints.child;
        }

        if (typeof constraints === 'object' && constraints !== null && 'l_operand' in constraints) {
            return [constraints];
        }

        return [];
    };

    const nodeContent = () => {
        const subjectConstraints = data?.general_properties?.subjectConstraints || [];

        return subjectConstraints.map((constraint, index) => {
            const childConstraints = getChildConstraints(constraint);
            const shouldWrapChildConstraints = childConstraints.length > 1;

            if (!childConstraints.length) {
                return null;
            }

            return (
                <div key={index}>
                    <div className="bg-white border border-gray-300 rounded-md flex flex-wrap items-center justify-center gap-x-1 gap-y-1 p-2 text-sm leading-4 max-w-full">
                        {shouldWrapChildConstraints && <span className="mr-1 text-gray-500">(</span>}

                        {childConstraints.map((child, subIndex) => (
                            <span key={subIndex} className="inline-flex flex-wrap items-center gap-1 max-w-full">
                                <span className="font-medium text-gray-700 break-all">{formatLeftOperand(child?.l_operand?.name)}</span>
                                <span className="text-gray-700">{child.operator}</span>
                                {normalizeDisplayOperand(child.r_operand)}
                                {hasConjunction(child.conjunction) && (
                                    <span className="font-semibold text-gray-500">{child.conjunction}</span>
                                )}
                            </span>
                        ))}

                        {shouldWrapChildConstraints && <span className=" text-gray-500">)</span>}
                    </div>
                    {subjectConstraints.length > 1 && hasConjunction(constraint.conjunction) && (
                        <div className="font-semibold text-gray-500 text-sm flex items-center justify-center m-2">{constraint.conjunction}</div>
                    )}
                </div>
            );
        });
    };

    const nodeHandles = ({ headerAnchorY } = {}) => {
        const handleClassName = `drg-handle ${selected ? 'drg-selected-handle' : ''}`;

        const sideHandleStyle = typeof headerAnchorY === 'number'
            ? { top: `${headerAnchorY}px` }
            : undefined;

        return (
            <div>
                <Handle
                    id="top-target"
                    type="target"
                    position={Position.Top}
                    className={handleClassName}
                    isConnectable={false}
                />
                {/* {(openSimulatorDRG || openPreviewDRG) ?
                    <CustomHandle
                        theme={theme}
                        selected={selected}
                        onAddNode={handleAddNodeFromNode}
                    /> :
                    <Handle
                        id="bottom-source"
                        type="source"
                        position={Position.Bottom}
                        className={handleClassName}
                        isConnectable={false}
                    />
                } */}
                <CustomHandle
                    selected={selected}
                    onAddNode={handleAddNodeFromNode}
                />
                <Handle
                    id="left-target"
                    type="target"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="left-source"
                    type="source"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-target"
                    type="target"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-source"
                    type="source"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
            </div>
        )
    };

    return (
        <ReusableNodes
            selected={selected}
            highlighted={data?.highlighted}
            focusMode={data?.focusMode}
            backgroundColor="#c8dbff"
            onClick={handleNodeClick}
            onDoubleClick={handleDoubleClick}
        >
            {(data?.warning === true && warningText.length > 0) && (
                <NodeWarning>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Warning" listDetails={warningText} />} arrow placement="top" tooltipcolorstyle={theme.palette.warning.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.warning.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeWarning>
            )}
            {(data?.error === true || errorText.length > 0) && (
                <NodeError>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Error" listDetails={errorText} />} arrow placement="top" tooltipcolorstyle={theme.palette.error.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.error.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeError>
            )}
            <NodeHeader>
                <div className="drg-icon bg-white">
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        fill="none"
                        preserveAspectRatio="xMidYMid meet"
                        className="block h-6 w-6"
                    >
                        <path stroke="none" d="M11.999989 12.000001C12.902107 12.000001 13.784001 11.736089 14.534124 11.241565C15.284195 10.747095 15.868824 10.0443 16.214054 9.221965C16.55923 8.399683 16.649548 7.494876 16.473519 6.62195C16.297489 5.749024 15.863054 4.947203 15.225113 4.317881C14.587171 3.688558 13.774418 3.260008 12.889612 3.08642C12.004754 2.912831 11.087653 3.002003 10.2542 3.342659C9.420747 3.683312 8.708429 4.260154 8.207283 5.000224C7.706141 5.740299 7.438698 6.610366 7.438778 7.500403C7.438884 8.693824 7.919489 9.838306 8.77487 10.682136C9.630236 11.525965 10.790336 12.000001 11.999989 12.000001ZM11.999989 13.285783C9.172824 13.285783 3.5294 15.008489 3.5294 18.428436V21.000001H20.470578V18.428436C20.470578 15.008489 14.827154 13.287371 11.999989 13.287371V13.285783Z" fill="#3B67E2" />
                    </svg>
                </div>
                <div className="drg-label">
                    <Typography variant="body2" className="text-gray-900" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: 14 }}>
                        {description}
                    </Typography>
                </div>
            </NodeHeader>
            <NodeContent>
                {nodeContent()}
            </NodeContent>
            <NodeHandles>
                {nodeHandles}
            </NodeHandles>
        </ReusableNodes>
    );
}